export type LoadProfileType = 'H0' | 'G0' | 'G1' | 'G2' | 'G3' | 'G4' | 'G5' | 'G6' | 'L0' | 'L1' | 'L2';

/**
 * Anschlussart of a Hausanschluss — what sits behind the connection. The load profile says HOW
 * it consumes over the day, the kind says WHAT it is (doc/Architektur/ANSCHLUSSART.md). Backend
 * ids (ConnectionKinds.cs); an element without the field is a household.
 */
export type ConnectionKind = 'household' | 'commercial' | 'mixed' | 'rlm' | 'connection_pillar' | 'generation';

export const CONNECTION_KIND_LABELS: Record<ConnectionKind, string> = {
  household: 'Haushalt',
  commercial: 'Gewerbe',
  mixed: 'Haushalt + Gewerbe',
  rlm: 'RLM-Kunde',
  connection_pillar: 'Anschlusssäule',
  generation: 'Erzeugungsanlage',
};

/** Short code for badges and table cells. */
export const CONNECTION_KIND_SHORT: Record<ConnectionKind, string> = {
  household: 'HH',
  commercial: 'G',
  mixed: 'H+G',
  rlm: 'RLM',
  connection_pillar: 'Säule',
  generation: 'Erz.',
};

/** Marker glyph inside the house circle — a letter/symbol per kind so the map reads the kind at a glance. */
export const CONNECTION_KIND_GLYPH: Record<ConnectionKind, string> = {
  household: '⌂',
  mixed: '⌂',
  commercial: 'G',
  rlm: 'R',
  connection_pillar: '▮',
  generation: '∿',
};

export const CONNECTION_KIND_BADGE_CLASS: Record<ConnectionKind, string> = {
  household: 'bg-gray-100 text-gray-600 dark:bg-primary-600 dark:text-primary-200',
  commercial: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  mixed: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  rlm: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  connection_pillar: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  generation: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

export function connectionKindOf(ha: { connectionKind?: ConnectionKind | string | null }): ConnectionKind {
  const k = ha.connectionKind;
  return k && k in CONNECTION_KIND_LABELS ? (k as ConnectionKind) : 'household';
}

export type AnyNetworkElement = TrafoStation | Abgangsleitung | Muffe | Kabelverteilschrank | Hausanschluss;

export interface NetworkElement {
  id: string;
  type: NetworkElementType;
  name: string;
  description: string;
  metadata: Record<string, unknown>;
  switchedOn: boolean;
  coordinates: Coordinates;
  index: number;
}

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface Waypoint {
  lat: number;
  lng: number;
}

export interface Segment {
  id: string;
  startCoordinate: Coordinates;
  endCoordinate: Coordinates;
  waypoints: Waypoint[];
  length: number;
  loadingPercent?: number;
  currentA?: number;
  currentFromA?: number;
  currentToA?: number;
  powerKW?: number;
  powerKVAR?: number;
  powerKVA?: number;
  fromBusVoltagePu?: number;
  toBusVoltagePu?: number;
  fromBusVoltageKv?: number;
  toBusVoltageKv?: number;
  // PandaPower topology indices from cable_segments table
  fromBus?: number;
  toBus?: number;
  lineIndex?: number;
  inService?: boolean;
  // Multi-busbar support: which busbar this segment connects to
  fromBusbarId?: string;
  toBusbarId?: string;
}

export interface Abgangsleitung extends NetworkElement {
  type: 'abgangsleitung';
  waypoints: Waypoint[];
  crossSection: number;
  totalLength: number;
  segments: Segment[];
  cableTypeString: string;
  cableType: string;
  sourceId?: string;
  targetId?: string;
  isEnabled: boolean;
  isRingCircuit?: boolean;
  ringCircuitId?: string;
  fuse?: Fuse;
  /** Betriebsfaktor 0,3–1,0 (operational / rated current capacity, §14a trigger); undefined = 1,0. Migration 0061. */
  operationalRatingFactor?: number;
}

export interface Muffe extends NetworkElement {
  type: 'muffe';
  connectedLines: ConnectedLine[];
  // PandaPower electrical properties
  busIndex?: number;
  inService?: boolean;
}

export interface Voltage {
  primary: number;
  secondary: number;
}

export interface CableSegment {
  id: string;
  index: number;
  length: number;
}

export interface ConnectedLine {
  id: string;
  cableSegments: CableSegment[];
  crossSection: number;
  MeLoID: string;
  isEnabled: boolean;
  totalPower?: number;
  fuse?: Fuse;
  metadata?: Record<string, unknown>;
}

export interface Fuse {
  current: number;
  type: string;
  isInserted: boolean;
}

export interface TrafoStation extends NetworkElement {
  type: 'trafostation';
  capacity: number;
  // Voltage is no longer configurable - PandaPower std_type defines voltages (10/0.4 kV)
  connectedLines: ConnectedLine[];
  connectedLoadPoints?: string[];  // Array of load point UUIDs
  fuses: Record<string, Fuse>;
  powerKW?: number;
  currentA?: number;
  loadingPercent?: number;
  // PandaPower electrical properties
  busIndexHv?: number;  // High voltage bus index
  busIndexLv?: number;  // Low voltage bus index
  inService?: boolean;
  /** Betriebsfaktor 0,3–1,0 (operational / rated capacity, §14a trigger); undefined = 1,0. Migration 0061. */
  operationalRatingFactor?: number;
}

/** Individual busbar within a Kabelverteilschrank (multi-busbar support) */
export interface KvsBusbar {
  id: string;
  kvsId: string;
  name: string;
  busbarIndex: number;
  busIndex?: number;
  inService: boolean;
  maxCurrentA?: number;
  description?: string;
  connectedLines: ConnectedLine[];
}

/** Coupling switch between two busbars in a Kabelverteilschrank */
export interface KvsBusbarSwitch {
  id: string;
  kvsId: string;
  busbarAId: string;
  busbarBId: string;
  name?: string;
  isClosed: boolean;
  switchType: string;
}

export interface Kabelverteilschrank extends NetworkElement {
  type: 'kabelverteilschrank';
  connectedLines: ConnectedLine[];
  /** Fuse per connected cable id (backend Kabelverteilschrank.Fuses). */
  fuses?: Record<string, Fuse>;
  hasRingCircuit?: boolean;
  // PandaPower electrical properties
  busIndex?: number;
  inService?: boolean;
  // Multi-busbar support
  busbars: KvsBusbar[];
  busbarSwitches: KvsBusbarSwitch[];
}

/** analog = FNN-Steuerbox (Relais), digital = Energiemanager, hybrid = Relais + digitale Schnittstelle. */
export type SteuerboxType = 'analog' | 'digital' | 'hybrid';

/** Herkunft einer Stammdatenzeile (Migration 0060); ein Sync überschreibt nie eine manuelle Zeile. */
export type ControlMasterDataSource = 'manual' | 'wilken' | 'maas' | 'import';

/** Steuerweg zur Anlage: Relais W4 (binär), EEBUS, EMS oder unbekannt. */
export type Steuerpfad = 'relay' | 'eebus' | 'ems' | 'unknown';

/** Relaisstufe eines CLS-Blocks: ab `thresholdW` stehen die Relais wie angegeben. */
export interface SteuerboxChannelLevel {
  thresholdW: number;
  s1: boolean;
  s2: boolean;
  w3: boolean;
  w4: boolean;
}

/** CLS-Block 1–4 einer Steuerbox (= eine steuerbare Ressource). Leistungen in Watt. */
export interface SteuerboxChannel {
  id: string;
  clsIndex: number;
  direction: 'consumption' | 'generation' | 'both';
  coding: 'binary' | 'fixed' | 'eebus' | 'unknown';
  minPowerW?: number;
  maxPowerW?: number;
  levels: SteuerboxChannelLevel[];
  assetId?: string;
  srId?: string;
  neloId?: string;
  schedulePrefix?: string;
  lastConfigSyncAt?: string;
  source: ControlMasterDataSource;
}

export interface Steuerbox {
  id: string;
  type: SteuerboxType;
  numRelays?: number;  // Only for analog type
  name?: string;  // Optional display name
  linkedTechnicalResourceIds: string[];  // IDs of technical resources controlled by this Steuerbox
  // § 14a-Stammdaten (Migration 0060, doc/Architektur/STEUERUNG.md 4.1)
  /** Steuerbox-ID, wie maas und der CLS-Dienst sie adressieren (z. B. GSWIC025070007). */
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  fnnVersion?: string;
  /** Seriennummer des SMGW, hinter dem die Box sitzt. */
  smgwGatewayId?: string;
  steuerpfad?: Steuerpfad;
  /** Kalenderdatum (yyyy-MM-dd), kein Zeitpunkt. */
  installedAt?: string;
  /** Kalenderdatum (yyyy-MM-dd); ausgebaute Boxen bleiben erhalten. */
  removedAt?: string;
  isOnline?: boolean;
  lastSeenOnlineAt?: string;
  source?: ControlMasterDataSource;
  sourceKey?: string;
  sourceSyncedAt?: string;
  /** Fehlt das Feld beim Speichern, bleiben die gespeicherten Kanäle unverändert. */
  channels?: SteuerboxChannel[];
}

/** Individual TAF profile (meter) information */
export interface TafProfileInfo {
  /** 0/null = identity, 1 = reported L1→L2→L3→L1, 2 = inverse. When set,
   * the per-phase fields below have already been rotated by the backend. */
  phaseRotation?: number | null;
  tafLogicalName: string;
  /** Messlokation (MeLo) dieses Zählers, sofern die Registry sie pro TAF-Profil liefert. */
  meLoId?: string;
  /** Zählernummer des physischen Zählers hinter diesem TAF-Profil. Ein Zähler
   * kann mehrere TAF-Profile tragen (z.B. TAF-7 + TAF-10) — über dieses Feld
   * werden Profile pro Zähler gruppiert. Null bei älteren Registry-Versionen. */
  meterDeviceNumber?: string;
  powerW?: number;
  timestamp?: string;
  tafType?: string;
  /** MeLo, auf die das Backend diesen TAF-10-Wert gebucht hat (HouseholdCurrentPowerService) — die Anzeige zeigt ihn
   * genau dort und leitet die Zuordnung nicht selbst ab. Fehlt bei Häusern ohne MeLos und bei Werten im Haus-Topf. */
  attributedMeLoId?: string;
  // Total power values (W)
  totalActivePowerW?: number;
  activePowerImportW?: number;
  activePowerExportW?: number;
  // Per-phase voltage (V)
  voltageL1V?: number;
  voltageL2V?: number;
  voltageL3V?: number;
  // Per-phase current (A)
  currentL1A?: number;
  currentL2A?: number;
  currentL3A?: number;
  // Per-phase active power (W)
  activePowerL1W?: number;
  activePowerL2W?: number;
  activePowerL3W?: number;
  // Power factor (cos φ)
  powerFactorTotal?: number;
  powerFactorL1?: number;
  powerFactorL2?: number;
  powerFactorL3?: number;
  // Frequency (Hz)
  frequencyHz?: number;
}

export interface SmartMeterGateway {
  id: string;
  serialNumber: string;
  manufacturer?: string;
  model?: string;
  firmwareVersion?: string;
  isActive?: boolean;
  lastCommunication?: string;
  /** Indicates whether this SMGW has realtime TAF data available */
  hasRealtimeData?: boolean;
  /** Timestamp of the last realtime measurement received */
  lastMeasurementTime?: string;
  /** Current power in Watts from realtime measurement */
  currentPowerW?: number;
  /** Number of TAF profiles (meters) aggregated into this measurement */
  tafProfileCount?: number;
  /** Confirmed phase rotation applied to live measurements from this SMGW.
   * 0/null = identity, 1 = reported L1→L2→L3→L1, 2 = inverse. */
  phaseRotation?: number | null;
  /** Individual TAF profiles with their power values */
  tafProfiles?: TafProfileInfo[];
  // Per-phase voltage (V)
  voltageL1V?: number;
  voltageL2V?: number;
  voltageL3V?: number;
  // Per-phase current (A)
  currentL1A?: number;
  currentL2A?: number;
  currentL3A?: number;
  // Per-phase active power (W)
  activePowerL1W?: number;
  activePowerL2W?: number;
  activePowerL3W?: number;
  // Power factor (cos φ)
  powerFactorTotal?: number;
  powerFactorL1?: number;
  powerFactorL2?: number;
  powerFactorL3?: number;
  // Grid frequency (Hz)
  frequencyHz?: number;
}

/** `other`: steuerbare Einrichtung ohne bekannten Gerätetyp (z. B. § 14a-TR aus Wilken ohne Wärme-/E-Mobilitätsnutzung). */
export type SRType = 'pv_anlage' | 'battery_storage' | 'heat_pump' | 'wallbox' | 'air_conditioning' | 'other';

export interface TechnischeRessource {
  id: string;
  type: SRType;
  name?: string;
  description?: string;
  metadata?: Record<string, unknown>;
  maxPower: number;
  steuerboxId?: string;
  relayNumber?: number;

  // PV Anlage specific properties
  solarPanelAreaM2?: number;
  efficiencyPercent?: number;
  tiltAngleDegrees?: number;
  azimuthDegrees?: number;
  currentPowerGenerationKw?: number;
  lastCalculationTime?: string;
  installationDate?: string;
  manufacturer?: string;
  model?: string;

  // Phase-specific power values (for PV systems with multi-phase output)
  powerPhaseL1Kw?: number;
  powerPhaseL2Kw?: number;
  powerPhaseL3Kw?: number;

  // Battery storage specific properties
  storageCapacityKwh?: number;

  // Heat pump specific properties
  auxiliaryHeaterPowerKw?: number;

  // § 14a-Stammdaten (Migration 0060, doc/Architektur/STEUERUNG.md 4.1)
  /** CLS-Block, der diese Ressource steuert. */
  channelId?: string;
  /** Technische Ressource (MaKo, D…). */
  trId?: string;
  /** Steuerbare Ressource (C…), Adressierung bei Direktansteuerung. */
  srId?: string;
  /** Netzlokation (E…), Adressierung bei EMS. */
  neloId?: string;
  /** Angemeldete SteuVE nach § 14a EnWG; fehlt = unbekannt. */
  participates14a?: boolean;
  controlKind?: 'direct' | 'ems' | 'none';
  /** § 14a regime (BK6-22-300 Ziff. 10): only "neu" is controlled by GridPilot. */
  regime?: 'neu' | 'alt_14a_bis_2028' | 'nachtspeicher_alt' | 'nicht_steuerbar_10_6';
  /** Date of the switch into the current regime (yyyy-MM-dd). */
  regimeChangedAt?: string;
  ratedPowerW?: number;
  ratedGenerationW?: number;
  storageCapacityWhRated?: number;
  /** Vom Installateur eingestellte Mindestleistung der Anlage (Wirkung eines Relaisbefehls). */
  deviceMinPowerW?: number;
  /** Kalenderdatum (yyyy-MM-dd), kein Zeitpunkt. */
  commissionedAt?: string;
  source?: ControlMasterDataSource;
  sourceKey?: string;
  sourceSyncedAt?: string;
}

export interface MeLoId {
  id: string;
  annualConsumption: number;
  /** Zugehörige Marktlokation (MaLo), vom GIS-Import (MeLo:kWh:MaLo) oder ERP-Sync befüllt. */
  malo?: string;
  /** Herkunft der Zuordnung: 'import' (GIS-Import) | 'gateway' | 'address' | 'manual' (ERP). */
  source?: 'import' | 'gateway' | 'address' | 'manual' | string;
  /** Aktuelle Leistung dieses Zählers (W), berechnet im Backend
   * (HouseholdCurrentPowerService). Undefined, wenn nicht einzeln zuordenbar
   * (dann siehe Hausanschluss.meloCurrentPool). */
  currentPowerW?: number;
  /** 'measured' = TAF10-Messwert, 'estimated' = Schätzung, 'pooled' = im Haus-Topf. */
  currentPowerSource?: 'measured' | 'estimated' | 'pooled';
}

/** House-level fallback for current power when live readings cannot be attributed
 * per meter (registry delivers no metering_location_id per TAF profile). */
export interface MeloCurrentPool {
  /** Sum of measured single-meter readings that could not be mapped to a MeLo (W). */
  measuredPowerW?: number;
  measuredCount: number;
  /** Estimated current power per SMGW-less meter (W), from the average annual consumption. */
  estimatedPerMeterW?: number;
  estimatedCount: number;
}

export interface Hausanschluss extends NetworkElement {
  type: 'hausanschluss';
  street: string | null;
  houseNumber: string | null;
  city: string | null;
  meLoId: string;
  melos: MeLoId[];
  neLoId: string;
  connectionPower: number;
  simulatedPowerPercent?: number | null;
  loadProfile: LoadProfileType;
  customLoadProfileCode?: string;
  /** Anschlussart (backend omits nothing here, but older payloads may lack it → household). */
  connectionKind?: ConnectionKind;
  annualConsumption: number;
  smgws: SmartMeterGateway[];
  technischeRessourcen: TechnischeRessource[];
  steuerboxes: Steuerbox[];
  connectedLine?: ConnectedLine;
  powerKW?: number;
  currentA?: number;
  loadingPercent?: number;
  // Household information
  householdCount?: number;
  loadPerHouseholdFactor?: number;
  /** Set only when per-meter current power cannot be attributed; see MeloCurrentPool. */
  meloCurrentPool?: MeloCurrentPool;
  // PandaPower electrical properties
  busIndex?: number;
  inService?: boolean;
}

export type NetworkElementType = 'abgangsleitung' | 'muffe' | 'trafostation' | 'kabelverteilschrank' | 'hausanschluss';

// Type guards
export function isAbgangsleitung(element: NetworkElement | undefined | null): element is Abgangsleitung {
  return element !== undefined && element !== null && element.type === 'abgangsleitung';
}

export function isTrafoStation(element: NetworkElement | undefined | null): element is TrafoStation {
  return element !== undefined && element !== null && element.type === 'trafostation';
}

export function isMuffe(element: NetworkElement | undefined | null): element is Muffe {
  return element !== undefined && element !== null && element.type === 'muffe';
}

export function isKabelverteilschrank(element: NetworkElement | undefined | null): element is Kabelverteilschrank {
  return element !== undefined && element !== null && element.type === 'kabelverteilschrank';
}

export function isHausanschluss(element: NetworkElement | undefined | null): element is Hausanschluss {
  return element !== undefined && element !== null && element.type === 'hausanschluss';
}

// Utility type guards
export function hasCoordinates(obj: unknown): obj is { coordinates: Coordinates } {
  return obj !== null && 
         obj !== undefined &&
         typeof obj === 'object' && 
         'coordinates' in obj &&
         typeof (obj as Record<string, unknown>).coordinates === 'object' &&
         (obj as Record<string, unknown>).coordinates !== null &&
         'lat' in ((obj as Record<string, unknown>).coordinates as Record<string, unknown>) &&
         'lng' in ((obj as Record<string, unknown>).coordinates as Record<string, unknown>) &&
         typeof ((obj as Record<string, unknown>).coordinates as Record<string, unknown>).lat === 'number' &&
         typeof ((obj as Record<string, unknown>).coordinates as Record<string, unknown>).lng === 'number';
}

export function hasWaypoints(obj: unknown): obj is { waypoints: Waypoint[] } {
  return obj !== null && 
         obj !== undefined &&
         typeof obj === 'object' && 
         'waypoints' in obj &&
         Array.isArray((obj as Record<string, unknown>).waypoints) &&
         ((obj as Record<string, unknown>).waypoints as unknown[]).every((wp: unknown) => 
           wp !== null &&
           wp !== undefined &&
           typeof wp === 'object' &&
           'lat' in (wp as Record<string, unknown>) &&
           'lng' in (wp as Record<string, unknown>) &&
           typeof (wp as Record<string, unknown>).lat === 'number' &&
           typeof (wp as Record<string, unknown>).lng === 'number'
         );
}
